import React from 'react';
import { Tag, Tooltip, Space, Button, message } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';

interface RefChipProps {
  label?: string;
  value: string;
  onOpen?: () => void;
  copyValue?: string; // Optional separate value for clipboard
  tooltip?: string;
  shortLength?: number; // How many chars to show (default 8)
  type?: 'default' | 'primary' | 'success' | 'warning' | 'error';
}

/**
 * S-H5: Consistent reference chip component
 * Displays IDs with copy-to-clipboard and optional navigation
 */
const RefChip: React.FC<RefChipProps> = ({
  label,
  value,
  onOpen,
  copyValue,
  tooltip,
  shortLength = 8,
  type = 'default',
}) => {
  const displayValue = value.length > shortLength ? `${value.slice(-shortLength)}` : value;
  const fullValue = copyValue || value;
  const tooltipText = tooltip || fullValue;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(fullValue);
    message.success(`${label || 'ID'} copied to clipboard`);
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onOpen) {
      onOpen();
    }
  };

  return (
    <Tooltip title={tooltipText}>
      <Space size={4}>
        {label && <span style={{ fontSize: 12, color: '#8c8c8c' }}>{label}:</span>}
        <Tag color={type} style={{ margin: 0, fontFamily: 'monospace', fontSize: 11 }}>
          {displayValue}
        </Tag>
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={handleCopy}
          style={{ padding: '0 4px', height: 20 }}
        />
        {onOpen && (
          <Button
            type="text"
            size="small"
            icon={<LinkOutlined />}
            onClick={handleOpen}
            style={{ padding: '0 4px', height: 20 }}
          />
        )}
      </Space>
    </Tooltip>
  );
};

export default RefChip;
